// src/pages/Contact.js
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet";
import {
  submitContactForm,
  clearContactStatus,
} from "../../slice/contactSlice";
import {
  FaUser,
  FaPhoneAlt,
  FaRegEnvelope,
  FaRegPaperPlane,
} from "react-icons/fa";
import { useDarkMode } from "../../hooks/useDarkMode";

const Contact = () => {
  const { isDarkMode } = useDarkMode();
  const dispatch = useDispatch();
  const { loading, success, error } = useSelector((state) => state.contact);

  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    subject: "",
    message: "",
  });

  const [errors, setErrors] = useState({});

  /* ================= FORM LOGIC ================= */
  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name || formData.name.length < 3)
      newErrors.name = "Name must be at least 3 characters.";
    if (!/^\d{10}$/.test(formData.mobile))
      newErrors.mobile = "Enter a valid 10-digit mobile number.";
    if (!formData.subject) newErrors.subject = "Subject is required.";
    if (!formData.message) newErrors.message = "Message is required.";
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      dispatch(submitContactForm(formData));
    }
  };

  useEffect(() => {
    if (success || error) {
      if (success) {
        setFormData({ name: "", mobile: "", subject: "", message: "" });
        setErrors({});
      }
      setTimeout(() => dispatch(clearContactStatus()), 3000);
    }
  }, [success, error, dispatch]);

  /* ================= THEME ================= */
  const pageBg = isDarkMode ? "bg-gray-950" : "bg-white";
  const cardBg = isDarkMode ? "bg-gray-900" : "bg-white";
  const inputBg = isDarkMode
    ? "bg-gray-800 border-gray-700 text-gray-200"
    : "bg-white border-gray-300 text-gray-800";

  const labelText = isDarkMode ? "text-gray-300" : "text-gray-700";
  const subText = isDarkMode ? "text-gray-400" : "text-gray-600";
  const circleColor = isDarkMode ? "bg-green-500/10" : "bg-[#008300]/10";

  return (
    <>
      <Helmet>
        {/* Title */}
        <title>Contact Seo Up Rank | AI Powered SEO Platform Support</title>

        {/* Description */}
        <meta
          name="description"
          content="Contact Seo Up Rank for AI powered SEO tools, website optimization services, keyword research and site audit solutions."
        />

        {/* Keywords */}
        <meta
          name="keywords"
          content="Contact SEO company, SEO support, website optimization help, SEO tools support, AI SEO platform contact"
        />

        {/* Canonical */}
        <link rel="canonical" href="https://novarsis.ai/contact" />

        {/* Robots */}
        <meta name="robots" content="index, follow" />

        {/* Open Graph */}
        <meta property="og:title" content="Contact Seo Up Rank - SEO Support" />
        <meta
          property="og:description"
          content="Get in touch with Seo Up Rank for website SEO optimization and AI powered SEO solutions."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://novarsis.ai/contact" />
        <meta
          property="og:image"
          content="https://novarsis.ai/seo-banner.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Seo Up Rank" />
        <meta
          name="twitter:description"
          content="Reach our team for SEO tools, website audits and ranking improvements."
        />
        <meta
          name="twitter:image"
          content="https://novarsis.ai/seo-banner.png"
        />

        {/* Local Business Schema */}
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Seo Up Rank",
            "url": "https://novarsis.ai",
            "logo": "https://novarsis.ai/logo.png",
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+91-XXXXXXXXXX",
              "contactType": "customer support",
              "areaServed": "Worldwide",
              "availableLanguage": "English"
            }
          }
          `}
        </script>
      </Helmet>
      {/* ================= HEADER ================= */}
      <>
        {/* ================= HERO SECTION ================= */}
        <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
          {/* BACKGROUND VIDEO */}
          <video
            className="absolute inset-0 w-full h-full object-cover "
            src="/Images/video-contact.mp4"
            autoPlay
            loop
            muted
            playsInline
            poster="/Images/contact-banner.png"
          />

          {/* DARK OVERLAY */}
          <div className="absolute inset-0 bg-black/70"></div>

          {/* TEXT CONTENT */}
          <div className="relative z-10 text-center px-6 max-w-4xl ">
            <h1 className="text-4xl md:text-6xl font-bold text-white">
              Contact <span className="text-[#008300]">Seo Up Rank</span>
            </h1>

            <p className="mt-4 text-lg md:text-xl text-gray-200">
              Contact Seo Up Rank for SEO services, AI powered optimization
              tools, keyword research, and website ranking improvements.
            </p>
          </div>
        </section>

        {/* ================= CONTACT SECTION ================= */}
        <section
          className={`relative overflow-hidden py-20 ${pageBg}`}
          itemScope
          itemType="https://schema.org/ContactPage"
        >
          {/* DECORATIVE CIRCLES */}
          <div
            className={`absolute -top-16 -left-16 w-40 h-40 ${circleColor} rounded-full`}
          />
          <div
            className={`absolute top-32 -right-20 w-48 h-48 ${circleColor} rounded-full`}
          />
          <div
            className={`absolute bottom-24 left-20 w-32 h-32 ${circleColor} rounded-full`}
          />
          <div
            className={`absolute -bottom-24 right-24 w-44 h-44 ${circleColor} rounded-full`}
          />

          <div className="relative z-10 max-w-4xl mx-auto px-4">
            {/* FORM CARD */}
            <div className={`rounded-3xl shadow-2xl p-8 md:p-12 ${cardBg}`}>
              <h2
                className={`text-3xl md:text-4xl font-bold mb-3 text-center ${
                  isDarkMode ? "text-white" : "text-gray-800"
                }`}
              >
                Get in <span className="text-[#008300]">Touch</span>
              </h2>

              <p className={`mb-10 text-center ${subText}`}>
                Have questions about SEO tools or website optimization? Send us
                a message and our SEO experts will contact you soon.
              </p>

              {success && (
                <p className="text-green-500 mb-6 text-center font-medium ">
                  ✔ Message sent successfully! 
                </p>
              )}

              {error && (
                <p className="text-red-500 mb-6 text-center font-medium">
                  {error}
                </p>
              )}

              {/* ================= FORM ================= */}
              <form onSubmit={handleSubmit} className="grid gap-6">
                {/* Name */}
                <div>
                  <label
                    className={`flex items-center gap-2 mb-2 ${labelText}`}
                  >
                    <FaUser className="text-[#008300]" /> Your Name
                  </label>

                  <input
                    type="text"
                    name="name"
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full p-3 rounded-lg border focus:outline-none ${inputBg}`}
                    required
                  />

                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                  )}
                </div>

                {/* Mobile */}
                <div>
                  <label
                    className={`flex items-center gap-2 mb-2 ${labelText}`}
                  >
                    <FaPhoneAlt className="text-[#008300]" /> Mobile Number
                  </label>

                  <input
                    type="tel"
                    name="mobile"
                    placeholder="+91 9876543210"
                    value={formData.mobile}
                    onChange={handleChange}
                    className={`w-full p-3 rounded-lg border focus:outline-none ${inputBg}`}
                    required
                  />

                  {errors.mobile && (
                    <p className="text-red-500 text-sm mt-1">{errors.mobile}</p>
                  )}
                </div>

                {/* Subject */}
                <div>
                  <label
                    className={`flex items-center gap-2 mb-2 ${labelText}`}
                  >
                    <FaRegEnvelope className="text-[#008300]" /> Subject
                  </label>

                  <input
                    type="text"
                    name="subject"
                    placeholder="Enter subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className={`w-full p-3 rounded-lg border focus:outline-none ${inputBg}`}
                  />

                  {errors.subject && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.subject}
                    </p>
                  )}
                </div>

                {/* Message */}
                <div>
                  <label className={`mb-2 block ${labelText}`}>Message</label>

                  <textarea
                    rows="4"
                    name="message"
                    placeholder="Write your message..."
                    value={formData.message}
                    onChange={handleChange}
                    className={`w-full p-3 rounded-lg border focus:outline-none ${inputBg}`}
                    required
                  />

                  {errors.message && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.message}
                    </p>
                  )}
                </div>

                {/* BUTTON */}
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-[#008300] to-green-600 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 hover:opacity-90 transition"
                >
                  <FaRegPaperPlane />
                  {loading ? "Sending..." : "Send Message"}
                </button>
              </form>

              {/* ================= CONTACT INFO ================= */}
              <div
                className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-center "
                itemScope
                itemType="https://schema.org/LocalBusiness"
              >
                <meta itemProp="name" content="Seo Up Rank" />

                {/* Email */}
                <div
                  className={`${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  <FaRegEnvelope className="mx-auto text-[#008300] text-2xl mb-2 " />
                  <p className="font-semibold">Email</p>
                  <p className="text-sm" itemProp="email">
                    info@novarsistech.com
                  </p>
                </div>

                {/* Phone */}
                <div
                  className={`${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  <FaPhoneAlt className="mx-auto text-[#008300] text-2xl mb-2 " />
                  <p className="font-semibold">Phone</p>
                  <p className="text-sm" itemProp="telephone">
                    +91 9111720303
                  </p>
                </div>

                {/* Address */}
                <div
                  className={`${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  <FaUser className="mx-auto text-[#008300] text-2xl mb-2 " />
                  <p className="font-semibold">Address</p>

                  <p
                    className="text-sm"
                    itemProp="address"
                    itemScope
                    itemType="https://schema.org/PostalAddress"
                  >
                    <span itemProp="streetAddress">Vijay Nagar</span>,{" "}
                    <span itemProp="addressLocality">Indore</span>,{" "}
                    <span itemProp="addressRegion">Madhya Pradesh</span>,{" "}
                    <span itemProp="addressCountry">India</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </>
    </>
  );
};

export default Contact;
